import {Injectable, OnInit} from '@angular/core';
import {ConfirmService, HttpLoaderService} from '@tymes4-shared';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import {AppSalesState} from '../models/app-sales-state';
import {
  PendingOrder,
  PendingOrderService,
  PendingOrderView,
  PendingReservationModel,
  PendingReservationService,
  PendingReservationView,
  PutPendingReservationLinesArgs
} from '../api';

@Injectable()
export class SalesHelperService implements OnInit {

  constructor(private loader: HttpLoaderService,
              private pendingOrderService: PendingOrderService,
              private confirmService: ConfirmService,
              private pendingReservationService: PendingReservationService,
              private translate: TranslateService) {
  }

  public state: AppSalesState = new AppSalesState();

  public pendingOrderKey = 'pendingOrderId';
  public renewalKey = 'isRenewal';
  private renewalSourceOrderKey = 'renewalSourceOrderId';
  private renewalPayByInvoiceKey = 'renewalPayByInvoice';
  private renewalReferenceKey = 'renewalReference';

  ngOnInit() {

  }

  public clearState(): any {
    this.state = new AppSalesState();
    sessionStorage.removeItem(this.pendingOrderKey);
    sessionStorage.removeItem(this.renewalKey);
    sessionStorage.removeItem(this.renewalSourceOrderKey);
    sessionStorage.removeItem(this.renewalPayByInvoiceKey);
    sessionStorage.removeItem(this.renewalReferenceKey);
  }

  public markRenewal(sourceOrderId: number, pendingOrderId: number, payByInvoice: boolean, reference: string | null | undefined): void {
    sessionStorage.setItem(this.renewalKey, '1');
    sessionStorage.setItem(this.pendingOrderKey, String(pendingOrderId));
    sessionStorage.setItem(this.renewalSourceOrderKey, String(sourceOrderId));
    sessionStorage.setItem(this.renewalPayByInvoiceKey, payByInvoice ? '1' : '0');
    if (reference != null) {
      sessionStorage.setItem(this.renewalReferenceKey, reference);
    } else {
      sessionStorage.removeItem(this.renewalReferenceKey);
    }
    this.state.renewalSourceOrderId = sourceOrderId;
    this.state.renewalPendingOrderId = pendingOrderId;
    this.state.renewalPayByInvoice = payByInvoice;
    this.state.renewalReference = reference ?? undefined;
  }

  public init() {
    this.state = new AppSalesState();
    this.restoreRenewalState();
  }

  private restoreRenewalState(): void {
    if (sessionStorage.getItem(this.renewalKey) !== '1') return;

    const pendingId = sessionStorage.getItem(this.pendingOrderKey);
    const sourceId = sessionStorage.getItem(this.renewalSourceOrderKey);
    const payByInvoice = sessionStorage.getItem(this.renewalPayByInvoiceKey);
    const reference = sessionStorage.getItem(this.renewalReferenceKey);

    if (pendingId != null) this.state.renewalPendingOrderId = +pendingId;
    if (sourceId != null) this.state.renewalSourceOrderId = +sourceId;
    if (payByInvoice != null) this.state.renewalPayByInvoice = payByInvoice === '1';
    if (reference != null) this.state.renewalReference = reference;
  }

  mapToServiceLines(orderLines) {
    if (!orderLines) {
      return null;
    }

    const result = [];

    for (const olPerTicketType of orderLines) {
      if (olPerTicketType.OrderLines != null) {

        for (const line of olPerTicketType.OrderLines) {

          result.push({
            ContingentId: line.ContingentId,
            PriceVariantId: line.PriceVariantId,
            TicketTypeId: line.TicketTypeId,
            Column: line.Column,
            NumberOfTickets: line.NumberOfTickets,
            Row: line.Row,
            // Renewal seeds carry these from the source order line so the new lines keep them.
            SelectedSingleEntranceId: line.SelectedSingleEntranceId ?? null,
            TicketTextId: line.TicketTextId ?? null,
            CustomerId: line.CustomerId ?? null,
          });
        }
      }
    }


    return result;
  }

  public handleSectionReservationLinesChanged(changeArgs, currentPendingReservation): Observable<any> {
    const payload: PutPendingReservationLinesArgs = {
      ParentVenueBuildingBlockId: changeArgs.parentVbbId,
      EventIds: changeArgs.eventIds,
      PassePartoutIds: changeArgs.passePartoutIds,
      CrossSellProductVariantIds: changeArgs.crossSellProductVariantIds,
      Lines: this.mapToServiceLines(changeArgs.orderLines)
    };

    this.loader.open();

    return new Observable((observer) => {

      if (currentPendingReservation == null) {
        //new order
        this.pendingReservationService.initializeNewPendingReservation().subscribe((reservation: PendingReservationModel) => {

          this.storeLinesForSection(payload, reservation).subscribe((reservation: PendingReservationView) => {
            observer.next(reservation);
            observer.complete();
          });

        });
      } else {
        this.storeLinesForSection(payload, currentPendingReservation).subscribe((reservation: PendingReservationView) => {
          observer.next(reservation);
          observer.complete();
        });
      }
    })

  }

  storeLinesForSection(payload, pendingReservation): Observable<any> {
    return new Observable((observer) => {
      //store the orderlines
      this.pendingReservationService.putPendingReservationLines(pendingReservation.Id, payload).subscribe((success: boolean) => {

        if (success) {

          //reget the order and details
          this.pendingReservationService.getPendingReservationDetails(pendingReservation.Id).subscribe((reservation: PendingReservationView) => {
            this.loader.close();
            observer.next(reservation);
            observer.complete();
          });
        } else {

          this.loader.close();

          this.confirmService.confirm({
            title: 'De regel is niet toegevoegd',
            message: `Het is niet gelukt de positie(s) aan de reservering toe te voegen. Normaal gesproken betekent dit dat de positie inmiddels bezet is.`,
            okonly: true
          }).subscribe((confirmed: boolean) => {
            observer.next(pendingReservation);
            observer.complete();
          });
        }
      });
    });
  }


  handleSectionOrderLinesChanged(changeArgs): Observable<any> {
    const payload: any = {
      ParentVenueBuildingBlockId: changeArgs.parentVbbId,
      EventIds: changeArgs.eventIds,
      PassePartoutIds: changeArgs.passePartoutIds,
      MembershipIds: changeArgs.membershipIds,
      CrossSellProductVariantIds: changeArgs.crossSellProductVariantIds,
      MatchDayPassId: changeArgs.matchDayPassId,
      OrderLines: changeArgs.matchDayPassId !== null && changeArgs.matchDayPassId !== undefined ? changeArgs.orderLines : this.mapToServiceLines(changeArgs.orderLines),
    };

    return new Observable((observer) => {

      if (this.state.order == null) {

        // new order
        let getNewPendingOrder: Observable<object>;

        if (this.state.selectedCustomer != null) {
          getNewPendingOrder = this.pendingOrderService.getNewForCustomer(this.state.selectedCustomer.Id);
        } else {
          getNewPendingOrder = this.pendingOrderService.getNewAnonymous();
        }

        getNewPendingOrder.subscribe((order: PendingOrder) => {

          sessionStorage.setItem('pendingOrderId', order.Id.toString());

          this.storeOrderLinesForSection(payload, order).subscribe((newOrder: PendingOrderView) => {
            this.state.order = newOrder;
            observer.next(newOrder);
            observer.complete();
          });

        });
      } else {
        this.storeOrderLinesForSection(payload, this.state.order).subscribe((newOrder: PendingOrderView) => {
          this.state.order = newOrder;
          observer.next(newOrder);
          observer.complete();
        });
      }
    });
  }

  storeOrderLinesForSection(payload, order): Observable<any> {
    return new Observable((observer) => {

      //store the orderlines
      this.pendingOrderService.putOrderLines(order.Id, payload).subscribe({
        next: (success: boolean) => {
          if (success) {
            this.pendingOrderService.getPendingOrderDetails(order.Id).subscribe({
              next: (updatedOrder: PendingOrderView) => {
                this.state.order = updatedOrder;
                observer.next(updatedOrder);
                observer.complete();
              },
              error: (err) => {
                this.loader.close();
                observer.error(err);
              },
            });

            return;
          }

          // Backend accepted the call but reported no placements could be allocated.
          this.showUnavailableSeatsMessage(order, observer, this.isRenewal());
        },
        error: (err) => {
          if (err?.status === 409) {
            const body = typeof err?.error === 'string' ? err.error : '';
            if (body.startsWith('TicketTypeNotAvailableOnPassePartout')) {
              this.showTicketTypeNotOnPassePartoutMessage(order, observer);
              return;
            }

            this.showUnavailableSeatsMessage(order, observer, this.isRenewal());
            return;
          }

          // Any other error (5xx, network, ...) is not a "seat unavailable" condition.
          // Surface it to the caller instead of misreporting it as a seat conflict.
          this.loader.close();
          observer.error(err);
        },
      });
    });
  }

  private isRenewal(): boolean {
    return sessionStorage.getItem(this.renewalKey) === '1';
  }

  private showUnavailableSeatsMessage(order, observer, useRenewalMessage: boolean) {
    this.loader.close();

    const titleKey = 'SALES.ORDERS.NEW.PUT-LINES.NOT-AVAILABLE.TITLE';
    const messageKey = useRenewalMessage
      ? 'SALES.ORDERS.NEW.RENEWAL.TARGET-POSITION-UNAVAILABLE'
      : 'SALES.ORDERS.NEW.PUT-LINES.NOT-AVAILABLE.MSG';

    this.confirmService.confirm({
      title: this.translate.instant(titleKey),
      message: this.translate.instant(messageKey),
      okonly: true
    }).subscribe(_ => {
      observer.next(order);
      observer.complete();
    });
  }

  private showTicketTypeNotOnPassePartoutMessage(order, observer) {
    this.loader.close();

    this.confirmService.confirm({
      title: this.translate.instant('SALES.ORDERS.NEW.PUT-LINES.NOT-AVAILABLE.TITLE'),
      message: this.translate.instant('SALES.ORDERS.NEW.PUT-LINES.TICKET-TYPE-NOT-ON-PASSEPARTOUT.MSG'),
      okonly: true
    }).subscribe(_ => {
      observer.next(order);
      observer.complete();
    });
  }
}





